import { Popover } from '@mui/material';


import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ContentTemplate,
  DescriptionLabel,
  Grid,
  HeaderTemplate,
  KeyValue,
  rowsPerPageOptionsDefault,
  SectionHeader,
  Stepper,
  Typography,
} from 'web-library';
import { candidateService } from '../../../../api';
import AddCandidate from '../../../../components/AddCandidate';
import { useWOGAA } from '../../../../hooks/wogaa';

import { CandidateResitRegistrationSchema } from '../../../../models/@candidate/candidateResitRegistrationSchema';
import { trueToRender } from '../../../../utils/ComponentUtils';
import {
  useCoordinatorResit,
  useCoordinatorResitSteps,
  useResitAccess,
} from '../../logic/useCoordinatorResit';
import { CoordinatorResitAction, useCandidateTable } from '../../logic/useCoordinatorResitData';
import ConfirmParticularLabel from '../ConfirmParticularLabel';
import ReviewPayLabel from '../ReviewPayLabel';
import SubjectSelectionLabel from '../SubjectSelectionLabel';
import CandidateConfirmParticular from './CandidateConfirmParticular';
import CandidateIndividualReview from './CandidateIndividualReview';
import CandidateListing from './CandidateListing';
import CandidateNavigateBar from './CandidateNavigateBar';
import CandidateSelectSubject from './CandidateSelectSubject';
import CoordinatorResitActionBar from './CoordinatorResitActionBar';
import GroupReview from './GroupReview';
import InstructionConsent from './InstructionConsent';
import { GroupResitSchema } from '../../../../api-refactored/group-resit-registration/model';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
const useStyles = makeStyles()((theme) =>
({
  stepContainer: {
    flex: 1,
    width: '100%',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    backgroundColor: theme.color.background.bgAlt,
  },
  contentPadding: {
    padding: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  leftButtonContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },

  leftPanel: {
    backgroundColor: theme.color.background.bgAlt,
  },
  rightPanel: {
    backgroundColor: theme.color.background.bgAlt,
  },
  sticky: {
    position: 'sticky',
    top: 0,
    zIndex: 150,
    backgroundColor: theme.color.background.bgAlt,
  },
  groupContainer: {
    paddingTop: theme.spacing(6),
  },
  stickLeft: {
    paddingLeft: 0,
  },
  popOver: {
    padding: theme.spacing(1.75),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    borderRadius: '16px',
    boxShadow: theme.shadow.shadow6,
    maxWidth: '465px',
    minHeight: '380px',
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

const showNoCandidateSelected = (selectedCandidate: number | null) => selectedCandidate === null;

interface CoordinatorResitRouteProps {
  defaultAdding?: boolean;
}

const useAnchor = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = useCallback((event: any) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return {
    anchorEl,
    open,
    handleClick,
    handleClose,
  };
};

const AddCandidatePopover = ({
  children,
  open,
  anchorEl,
  handleClose,
}: {
  children: React.ReactNode;
  open: boolean;
  anchorEl: any;
  handleClose: () => void;
}) => (
  <Popover
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    open={open}
    anchorEl={anchorEl}
    onClose={handleClose}
  >
    {children}
  </Popover>
);

const MultiSteps = ({
  step,
  selectedCandidate,
}: {
  step: number;
  selectedCandidate: number | null;
}) => {
  const { classes } = useStyles();

  if (step === 0 && selectedCandidate !== null) {
    return (
      <Box className={classes.contentPadding}>
        <SubjectSelectionLabel />
      </Box>
    ); // render subject selection label
  }

  if (step === 1 && selectedCandidate !== null) {
    return (
      <Box className={classes.contentPadding}>
        <ConfirmParticularLabel />
      </Box>
    );
  }

  if (step === 2 && selectedCandidate !== null) {
    return (
      <Box className={classes.contentPadding}>
        <ReviewPayLabel />
      </Box>
    );
  }

  return null;
};

const CoordinatorResitRoute = ({ defaultAdding = false }: CoordinatorResitRouteProps) => {
  const { classes } = useStyles();
  const routes = useCoordinatorResitSteps();
  const { t } = useTranslation();
  const [resitUuid, setResitUuid] = useState('');
  const { data: resitConfiguration } = candidateService.resit.coordinator.useResitConfiguration();
  const { examRedux, accessData } = useResitAccess(!!resitUuid);
  const [declareError, setDeclareError] = useState(false);
  const [groupFee, setGroupFee] = useState<GroupResitSchema | null>(null);
  // wogaa transaction start for coordinator resit flow
  const { transactionStart } = useWOGAA('myexamsseab-6475');
  transactionStart();
  const {
    step,
    setStep,
    state,
    dispatch,
    candidateTableRows,
    isAddCandidate,
    setIsAddCandidate,
    isAgreed,
    setIsAgreed,
    stepNavigate,
    continueStatus,
    feeProcessingError,
  } = useCoordinatorResit(defaultAdding, resitUuid);
  const {
    selectedCandidate,
    setSelectedCandidate,
    rowsSelected,
    setRowsSelected,
    options,
    tableColumns,
  } = useCandidateTable(candidateTableRows.length, rowsPerPageOptionsDefault);
  const handleUpdateNoOfCandidate = useCallback(
    (num: number) => dispatch({ type: 'UPDATE_NO_OF_CANDIDATE', payload: num }),
    [dispatch]
  );
  const { anchorEl, open, handleClick, handleClose } = useAnchor();

  return (
    <>
      <HeaderTemplate>
        <KeyValue
          keyText={t('resit-->CoordinatorResit.Examination')}
          valueText={`${examRedux.examLevel?.description ?? ''} ${dayjs().year()}`}
        />
      </HeaderTemplate>
      <InstructionConsent show={!resitUuid} setResitUuid={setResitUuid} />
      {resitUuid && (
        <>
          <HeaderTemplate sticky>
            <Stepper steps={routes} activeStep={step} />
          </HeaderTemplate>
          <ContentTemplate continuous isContinuable type="full">
            {step < 3 && (
              <Grid container>
                <Grid xs={4} className={classes.leftPanel}>
                  <CandidateListing
                    candidateNoLimit={resitConfiguration?.data.uploadLimit || 10}
                    dispatch={dispatch}
                    candidateTableRows={candidateTableRows}
                    rowsSelected={rowsSelected}
                    setRowsSelected={setRowsSelected}
                    options={options}
                    tableColumns={tableColumns}
                    selectedCandidate={selectedCandidate}
                    setSelectedCandidate={setSelectedCandidate}
                    isAddCandidate={isAddCandidate}
                    setIsAddCandidate={(evt: any, val: boolean) => {
                      handleClick(evt);
                      setIsAddCandidate(val);
                    }}
                    step={step}
                    setStep={setStep}
                  />
                  <AddCandidatePopover open={open} anchorEl={anchorEl} handleClose={handleClose}>
                    <Box className={classes.popOver}>
                      <AddCandidate
                        existingCandidates={state.candidateID}
                        onAddCandidate={(newCandidateData: CandidateResitRegistrationSchema) => {
                          dispatch({
                            type: 'ADD_CANDIDATE',
                            payload: newCandidateData,
                          } as CoordinatorResitAction);
                          setIsAddCandidate(false);
                        }}
                        handleClose={handleClose}
                      />
                    </Box>
                  </AddCandidatePopover>
                </Grid>
                <Grid xs={8} className={classes.rightPanel}>
                  {trueToRender(
                    showNoCandidateSelected(selectedCandidate),
                    <Box className={classes.contentPadding}>
                      <SectionHeader title="No candidate selected" />
                      <DescriptionLabel label="Select at least one candidate to proceed." />
                    </Box>
                  )}
                  <MultiSteps step={step} selectedCandidate={selectedCandidate} />
                  {selectedCandidate !== null && (
                    <CandidateNavigateBar
                      candidateName={
                        state.resitCandidates?.[candidateTableRows[selectedCandidate]?.id]
                          .statutoryName
                      }
                      upDisabled={selectedCandidate === 0}
                      downDisabled={selectedCandidate === candidateTableRows.length - 1}
                      onClickUp={() => setSelectedCandidate(selectedCandidate - 1)}
                      onClickDown={() => setSelectedCandidate(selectedCandidate + 1)}
                      pointer={selectedCandidate + 1}
                      totalNumber={candidateTableRows.length}
                    />
                  )}
                  {step === 0 && selectedCandidate !== null && (
                    <Box className={classes.contentPadding}>
                      <CandidateSelectSubject
                        key={selectedCandidate}
                        dispatch={dispatch}
                        selectedCandidate={selectedCandidate}
                        setSelectedCandidate={setSelectedCandidate}
                        candidateTableRows={candidateTableRows}
                        state={state}
                      />
                    </Box>
                  )}
                  {step === 1 && selectedCandidate !== null && (
                    <CandidateConfirmParticular
                      dispatch={dispatch}
                      selectedCandidate={selectedCandidate}
                      candidateTableRows={candidateTableRows}
                      state={state}
                    />
                  )}
                  {step === 2 && selectedCandidate !== null && (
                    <CandidateIndividualReview
                      selectedCandidate={selectedCandidate}
                      candidateTableRows={candidateTableRows}
                      resitUuid={resitUuid}
                      state={state}
                    />
                  )}
                </Grid>
              </Grid>
            )}
            {step === 3 && (
              <Grid xs={12} md={12}>
                <GroupReview
                  state={state}
                  isAgreed={isAgreed}
                  setIsAgreed={setIsAgreed}
                  declareError={declareError}
                  setGroupFee={setGroupFee}
                  setNoOfCandidate={handleUpdateNoOfCandidate}
                  enabledPayment={!!accessData?.payOnbehalf}
                  resitUuid={resitUuid}
                />
              </Grid>
            )}
            <Grid container>
              {step !== 3 && (
                <Grid xs={4}>
                  <></>
                </Grid>
              )}
              <Grid xs={8}>
                <Box
                  className={[classes.contentPadding, step === 3 ? classes.stickLeft : ''].join(
                    ' '
                  )}
                >
                  {feeProcessingError && (
                    <Typography variant="body2" className={classes.errString}>
                      {feeProcessingError}
                    </Typography>
                  )}
                  <CoordinatorResitActionBar
                    candidateID={state.candidateID}
                    candidatesDetails={state.resitCandidates}
                    dispatch={dispatch}
                    step={step}
                    stepNavigate={stepNavigate}
                    groupFee={groupFee}
                    setDeclareError={setDeclareError}
                    isAgreed={isAgreed}
                    continueStatus={continueStatus}
                    resitUuid={resitUuid}
                  />
                </Box>
              </Grid>
            </Grid>
          </ContentTemplate>
        </>
      )}
    </>
  );
};

export default CoordinatorResitRoute;
